import React, { useState } from "react";
import { shotLabel } from "../../utils/score";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Award, Calendar, ClipboardList, Clock3, Crosshair, Trophy } from "lucide-react";
import TargetMap from "../../components/TargetMap";
import MissingTargetNotice from "../../components/MissingTargetNotice";
import { EmptyState, Loading, Pagination, SectionCard, Tabs } from "../../components/ui";
import { useApi } from "../../api/client";
import { formatLength, formatVelocity, formatVelocitySpread, lengthLabel, useUnits } from "../../utils/units";
import { MatchInfo, StringRecord } from "../../data/types";

export default function ShotMapPage() {
  const { stringId } = useParams();
  const navigate = useNavigate();
  const isAdmin = useLocation().pathname.startsWith("/admin");
  const { data: s, loading } = useApi<StringRecord | null>(stringId ? `/strings/${stringId}` : null, null);
  // The scorecard prints the match name; the string only carries the match id.
  const { data: matchDoc } = useApi<MatchInfo | null>(s?.matchId ? `/matches/${s.matchId}` : null, null);
  const [selected, setSelected] = useState<number | null>(null);
  const [panel, setPanel] = useState("Statistics");
  const { units } = useUnits();
  // Only fetched to tell "none imported yet" from "this one name is missing".
  const { data: storedFaces } = useApi<{ name: string }[]>(s && !s.targetFace ? "/target-faces" : null, []);

  if (loading) return <div className="card m-4"><Loading /></div>;
  if (!s) {
    return (
      <div className="card m-4">
        <EmptyState
          title="Shot string not found"
          message="Strings are created when a shoot workbook is imported."
          action={
            <Link to="/admin/import" className="text-[13px] font-bold text-primary hover:underline">
              Go to Import Data
            </Link>
          }
        />
      </div>
    );
  }

  // Target Rifle counts centres as V, F-class as X.
  const centre = s.centreLabel || "V";
  const scoring = s.shots.filter((x) => !x.sighter);
  /*
   * Coordinates are stored in MOA. Turning them into inches or millimetres needs
   * the face's scale, which only a string with a target definition has — without
   * one, measurements stay in MOA whatever the reader has chosen.
   */
  const mmPerMoa = s.targetFace?.mmPerMoa ?? null;
  const lenLabel = lengthLabel(units.length, mmPerMoa);
  const len = (moa: number) => formatLength(moa, units.length, mmPerMoa);
  /*
   * Velocity is measured over EVERY shot, sighters included — they were fired
   * with the same ammunition, and dropping them would throw away part of the
   * sample. Shots the target recorded no velocity for are skipped rather than
   * counted as zero, which would drag the average down and inflate the spread.
   *
   * Kept in the unit the target recorded (m/s); converted only when printed.
   */
  const vel = (() => {
    const v = s.shots.map((x) => Number(x.velocity)).filter((n) => Number.isFinite(n) && n > 0);
    if (!v.length) return null;
    const avg = v.reduce((a, b) => a + b, 0) / v.length;
    const high = Math.max(...v);
    const low = Math.min(...v);
    return {
      avg,
      sd: Math.sqrt(v.reduce((a, b) => a + (b - avg) ** 2, 0) / v.length),
      high,
      low,
      es: high - low, // extreme spread
      count: v.length,
    };
  })();
  const cx = scoring.length ? scoring.reduce((a, x) => a + x.x, 0) / scoring.length : 0;
  const cy = scoring.length ? scoring.reduce((a, x) => a + x.y, 0) / scoring.length : 0;

  // A string of nothing but sighters has no group to measure.
  const ballistics = (() => {
    if (!scoring.length) {
      return { group: "—", meanRadius: "—", vertical: "—", horizontal: "—", radialSD: "—", centerDist: "—" };
    }
    const xs = scoring.map((p) => p.x);
    const ys = scoring.map((p) => p.y);
    const w = Math.max(...xs) - Math.min(...xs);
    const h = Math.max(...ys) - Math.min(...ys);
    const radii = scoring.map((p) => Math.hypot(p.x - cx, p.y - cy));
    const mean = radii.reduce((a, b) => a + b, 0) / radii.length;
    const radialSD = Math.sqrt(radii.reduce((a, b) => a + (b - mean) ** 2, 0) / radii.length);
    return {
      group: len(Math.hypot(w, h)),
      meanRadius: len(mean),
      vertical: len(h),
      horizontal: len(w),
      radialSD: len(radialSD),
      centerDist: len(Math.hypot(cx, cy)),
    };
  })();

  return (
    <div className={isAdmin ? "" : "mx-auto max-w-[1400px] px-5 py-4"}>
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-[13px] font-bold text-primary hover:underline">
        <ArrowLeft size={14} /> Back to Results
      </button>
      <h1 className="mt-1 text-lg font-bold tracking-tight text-ink">
        {s.shooter} — {s.distance} — {s.club}
      </h1>
      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-muted">
        <span className="flex items-center gap-1.5"><Calendar size={13} /> {s.date}</span>
        <span className="flex items-center gap-1.5"><Clock3 size={13} /> {s.time}</span>
        <span className="flex items-center gap-1.5"><Trophy size={13} /> {s.event}</span>
      </div>

      {/*
        The score, statistics and string details sit beside the target from
        1280px up rather than 1536px. Stacked, this page ran to four screens;
        alongside, the target and its numbers are read together.
      */}
      {/* The string carries its own geometry; without it, say so rather than
          drawing a target that could be misread as this shooter's. */}
      {!s.targetFace && (
        <MissingTargetNotice faces={[s.target].filter(Boolean)} storedCount={storedFaces.length} className="mt-3" />
      )}

      {/*
        The target across the top, everything else beneath it.

        It used to be one column of a two-column grid, sharing the page with a
        360px panel, so the thing people open this page to look at was drawn at
        about half the width available.
      */}
      <div className="mt-3 space-y-4">
          <SectionCard
            title={<span className="flex items-center gap-2"><Crosshair size={17} className="text-primary" /> Shot Map</span>}
            bodyClassName="p-4"
          >
            <TargetMap
              shots={s.shots}
              selectedShot={selected}
              onSelectShot={setSelected}
              velocityUnit={units.velocity}
              // Ring geometry as recorded for this string — see StringRecord.targetFace.
              face={s.targetFace ?? null}
              info={{
                sid: s.sid,
                shooter: s.shooter,
                rifleClass: s.rifleClassName ?? s.rifleClassTag,
                competition: s.event,
                match: matchDoc?.name ?? s.distance,
                stringNo: s.stringNo,
                totalScore: s.totalScore,
                vCount: s.vCount,
                centreLabel: s.centreLabel,
                sightersAllowed: matchDoc?.sighters,
                faceName: s.targetFace?.name ?? s.target,
              }}
            />
          </SectionCard>

        {/*
          The shot list and the statistics beside it, read as a pair: a row in
          one explains a figure in the other. Side by side from a laptop upward
          rather than only on a very wide screen — most of these are read on a
          1000-to-1200px window, which is below Tailwind's `xl`.
        */}
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
          <div className="min-w-0 space-y-3">
          <SectionCard
            title={<span className="flex items-center gap-2"><ClipboardList size={17} className="text-primary" /> Shot List</span>}
          >
            {/*
              Sized to hold a whole string without scrolling in either
              direction. A 20-shot match with its sighters is 22 rows, so that
              is the case to fit; the shot list is the one thing on this page
              that has to be read whole.

              The width comes from the column split beside it rather than from
              dropping columns: an even split gave a seven-column table the same
              room as a list of single figures, and only one of them needed it.
            */}
            <div className="max-h-[42rem] overflow-y-auto">
              <table className="w-full table-fixed">
                <thead>
                  <tr className="border-b border-line bg-slate-50">
                    <th className="th w-[12%]">Shot</th>
                    <th className="th w-[11%] text-right">Score</th>
                    <th className="th w-[14%] text-right">X ({lenLabel})</th>
                    <th className="th w-[14%] text-right">Y ({lenLabel})</th>
                    <th className="th w-[14%] text-right">R ({lenLabel})</th>
                    <th className="th w-[19%] text-right">Velocity ({units.velocity})</th>
                    <th className="th w-[16%] text-right">Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {s.shots.map((x, i) => (
                    <tr
                      key={x.n}
                      onClick={() => setSelected(selected === x.n ? null : x.n)}
                      className={`cursor-pointer transition-colors ${
                        selected === x.n ? "bg-primary-soft" : i % 2 === 1 ? "bg-slate-50/50 hover:bg-primary-soft/40" : "hover:bg-primary-soft/40"
                      }`}
                    >
                      <td className="td font-bold">{shotLabel(x)}</td>
                      <td className={`td text-right font-extrabold ${x.isV || x.score === 10 ? "text-danger" : ""}`}>
                        {/* the ring value exactly as the workbook recorded it — X stays X */}
                        {x.score}
                      </td>
                      <td className="td text-right">{formatLength(x.x, units.length, mmPerMoa)}</td>
                      <td className="td text-right">{formatLength(x.y, units.length, mmPerMoa)}</td>
                      {/* Radius from centre — the hypotenuse of the two beside it. */}
                      <td className="td text-right">{formatLength(Math.hypot(x.x, x.y), units.length, mmPerMoa)}</td>
                      <td className="td text-right">{formatVelocity(x.velocity, units.velocity)}</td>
                      <td className="td text-right text-muted">{x.time}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="border-t border-line">
              {/*

                Counted as impacts, then split — "12 shots" on a 2+10 string
                reads as a 12-shot score, when only the 10 count.
              */}
              <Pagination
                summary={`${s.shots.length} impacts — ${scoring.length} scoring, ${s.shots.length - scoring.length} sighter${
                  s.shots.length - scoring.length === 1 ? "" : "s"
                }`}
                pages={1}
              />
            </div>
          </SectionCard>
        </div>

          {/* Score, then the statistics and the string's provenance. */}
          <div className="min-w-0 space-y-3">
          <div className="card p-4">
            <h3 className="flex items-center gap-2 text-[15px] font-bold text-ink">
              <Award size={17} className="text-warning" /> Score
            </h3>
            <div className="mt-1 text-center">
              <div className="text-[40px] font-extrabold leading-none tracking-tight text-ink">
                {s.totalScore}.{s.vCount}
                {centre}
              </div>
              <div className="mt-3 flex items-center justify-center gap-10">
                <div>
                  <div className="text-xl font-extrabold text-ink">
                    {s.totalScore}
                    {s.possible ? <span className="text-sm text-muted"> / {s.possible}</span> : null}
                  </div>
                  <div className="text-xs font-medium text-muted">Total Score</div>
                </div>
                <div>
                  <div className="text-xl font-extrabold text-warning">{s.vCount}</div>
                  <div className="text-xs font-medium text-muted">{centre} Count</div>
                </div>
              </div>
              <div className="mt-3 border-t border-line pt-3">
                <span className="inline-block rounded-lg bg-warning-soft px-3.5 py-1 text-[13px] font-bold text-warning">
                  {s.event}
                </span>
              </div>
            </div>
          </div>

          {/*
            Statistics and the string's provenance are both reference panels for
            the same target, and stacking them made this column twice the height
            of the map beside it. One at a time keeps the whole card on screen.
          */}
          <div className="card">
            <div className="border-b border-line px-3 pt-3">
              <Tabs tabs={["Statistics", "String Info"]} active={panel} onChange={setPanel} />
            </div>

            <div className={panel === "Statistics" ? "px-5 py-3" : "hidden"}>
            <div className="divide-y divide-line text-sm">
              {[
                ["Target", s.targetFace?.name || s.target],
                ["Distance", s.distance],
                ...(s.rifleClassName ? [["Class", s.rifleClassName]] : []),
                ["Shot Count", `${scoring.length} scoring, ${s.shots.length - scoring.length} sighters`],
                ...(vel
                  ? [
                      ["Velocity Avg", formatVelocity(vel.avg, units.velocity, true)],
                      ["Velocity SD", formatVelocitySpread(vel.sd, units.velocity, true)],
                      ["Velocity ES", formatVelocitySpread(vel.es, units.velocity, true)],
                      [
                        "Velocity High / Low",
                        `${formatVelocity(vel.high, units.velocity)} / ${formatVelocity(vel.low, units.velocity, true)}`,
                      ],
                    ]
                  : [["Velocity", "Not recorded"]]),
              ].map(([l, v]) => (
                <div key={l} className="flex justify-between gap-3 py-1">
                  <span className="shrink-0 text-muted">{l}</span>
                  <span className="text-right font-bold text-ink">{v}</span>
                </div>
              ))}
              <div className="flex justify-between gap-3 py-1">
                <span className="shrink-0 text-muted">Group Centre</span>
                <span className="text-right font-bold text-success">{len(cx)}, {len(cy)} {lenLabel}</span>
              </div>
            </div>
            {/* Three across rather than two — the same six figures in half the height. */}
            <div className="py-3">
              <div className="text-center text-[11px] font-bold uppercase tracking-widest text-muted">Ballistics ({lenLabel})</div>
              <div className="mt-1.5 grid grid-cols-3 gap-x-3 gap-y-2 text-center">
                {[
                  [ballistics.group, "Group"],
                  [ballistics.meanRadius, "Mean Rad"],
                  [ballistics.vertical, "Vertical"],
                  [ballistics.horizontal, "Horizontal"],
                  [ballistics.radialSD, "Radial SD"],
                  [ballistics.centerDist, "Centre Dist"],
                ].map(([v, l]) => (
                  <div key={l}>
                    <div className="text-base font-extrabold text-ink">{v}</div>
                    <div className="text-[11px] text-muted">{l}</div>
                  </div>
                ))}
              </div>
            </div>
            </div>

            <div className={panel === "String Info" ? "px-5 py-3" : "hidden"}>
            <div className="divide-y divide-line text-sm">
              {/*
                Audit trail back to the workbook: identity is keyed by the log's
                SID column, and the operator-typed label is kept alongside it so
                any disagreement between the two stays visible on the record.
              */}
              {[
                ["String", `${s.stringNo} of ${s.stringsTotal}`],
                ["Position", s.position],
                ["Shooter", s.shooter],
                ["EID", s.eid],
                ["MID", s.mid],
                ["Frame", s.frame],
                ["Face", s.target],
                // Frame calibration exactly as the workbook recorded it
                ["Mic w×h (mm)", s.calibration && s.calibration.micW != null ? `${s.calibration.micW} × ${s.calibration.micH}` : null],
                ["Cal x / y (mm)", s.calibration && s.calibration.xCal != null ? `${s.calibration.xCal} / ${s.calibration.yCal}` : null],
                ["Conditions", s.conditions],
                ["SID column in file", s.sourceSid],
                ["Workbook label", s.sourceLabel],
              ]
                .filter(([, v]) => v)
                .map(([l, v]) => (
                  <div key={l} className="flex justify-between gap-4 py-1">
                    <span className="shrink-0 text-muted">{l}</span>
                    <span className="break-all text-right font-bold text-ink">{v}</span>
                  </div>
                ))}
            </div>
            </div>
          </div>

        </div>
        </div>
      </div>
    </div>
  );
}
